import type { IArticleConfig } from '~/config/article';
import { vdebugLog } from '~/debug';
import AdvertisingCreator from '~/modules/Advertisement';
import ServerLogger, { IRequestParams } from '~/modules/ServerLogger';
import Session from '~/modules/Session';
import { getFormattedTime, isiOSVersion } from '~/utils/general';
import { advancedKeys } from '~/config/keys';
import ADUnit from './ADUnit';
import { isInIframe } from '~/utils/dom';

/**
 * Prebid is an independent AD Unit that manages GPT loading and scripting by itself
 * All we need to do is just attach the script to document's head and create the ad and attach it to correct div
 * and then the prebid js will do the rest.
 */
class PrebidAdvertisementUnit extends ADUnit {
  /** Ad wrapper element */
  public wrapper: HTMLDivElement;
  /** Ad div element */
  private adDiv: HTMLElement;
  /** Is prebid script loaded or has the loading attempt finished */
  public static prebidLoaded: boolean = false;
  /** Is prebid script blocked from loading */
  public static prebidBlocked: boolean = false;
  /** Has enough time passed since prebid has loaded */
  public static initialPeriodPassed: boolean = false;
  /** If the ad is a GPT (Google Publisher Tag) ad or not */
  public static noGPT: boolean = false;

  /**
   * Initialize prebid ad
   * @param articleConfig
   * @param gptNumber
   * @param gptId
   * @param hideLabel
   * @param hideLabelLogo
   * @param numberOfSideAds
   * @param index
   * @param scrollingAd
   */
  constructor(
    protected readonly articleConfig: IArticleConfig,
    index: string = '',
    gptNumber?: string,
    gptId?: string,
    hideLabel = false,
    hideLabelLogo = false,
    numberOfSideAds = 0,
    protected readonly scrollingAd = false
  ) {
    super(hideLabelLogo, index === '');
    /** In case we have received a custom gpt id and/or a gpt override - the advertisement ID shall change */
    /** Otherwise we will use the number already generated in ADUnit's constructor */
    const gptOverride = typeof articleConfig.ads === 'object' && articleConfig.ads.gptOverride;
    const gptAdId = gptId || gptOverride || '1497448474263';
    this.advertisementID = gptNumber
      ? `div-gpt-ad-${gptAdId}-${gptNumber}`.concat(index)
      : `div-gpt-ad-${gptAdId}-${this.uniqueID}`.concat(index);
    /** For Desktop only, create side ads if configured */
    if (numberOfSideAds > 0 && !Session.isMobileUser) {
      this.createSideAds(hideLabel, numberOfSideAds);
    } else {
      const [adWrapper, adDiv] = this.createWrapperElement(hideLabel);
      this.wrapper = adWrapper;
      this.adDiv = adDiv;
    }

    if (AdvertisingCreator.canRefreshAds) {
      this.refreshAdUntilVisible();
    }

    if (advancedKeys[this.articleConfig.apiKey].trackUnfilledAds) {
      // After some time passes, report unfilled ads if there is still no ad iframe and if adblocker was not detected
      setTimeout(() => {
        if (!this.wrapper.offsetParent) {
          return;
        }
        import(/* webpackChunkName: "abd" */ './AdblockDetector').then((adblockDetector) => {
          if (!this.areAdsPresent() && !adblockDetector.default.adblockDetected) {
            vdebugLog('warn', true, `Vuukle - ${getFormattedTime()} [Platform.js] Unfilled ad -`, this.advertisementID);
            ServerLogger.log('unfilled_ads', articleConfig, [
              { key: 'unfilledAdId' as keyof IRequestParams, value: this.advertisementID },
            ]);
          }
        });
      }, 10000);
    }
  }

  /**
   * Creates a scrolling container with the ad inside of it
   * @param additionalElementToAdd
   */
  public constructInterscrollerAd(additionalElementToAdd?: HTMLElement) {
    const interscrollerWrapper = document.createElement('div');
    const interscrollerContainer = document.createElement('div');
    const interscrollerOverlay = document.createElement('div');
    const isInsideAnIframe = isInIframe();

    if (isInsideAnIframe) {
      interscrollerContainer.style.cssText = `
        height: 250px;
        overflow: scroll;
      `;
    } else {
      interscrollerWrapper.style.cssText = `
        position: relative;
        width: 100%;
        display: flex;
        justify-content: space-between;
        flex-direction: column;
      `;
      interscrollerContainer.style.cssText = `
        height: 250px;
        display: flex;
        justify-content: center;
      `;
      interscrollerOverlay.style.cssText = `
        position: absolute;
        height: 250px;
        width: 300px;
        clip-path: inset(0px calc(100% - 300px) calc(100% - 250px) 0px);
        display: flex;
        flex-direction: column;
      `;

      this.adDiv.style.cssText += `
        position: fixed;
        top: 50vh;
        transform: translate(0px, ${additionalElementToAdd ? '-20%' : '-50%'});
      `;
    }

    this.wrapper.style.cssText += `
      width: 300px;
    `;

    this.wrapper.appendChild(interscrollerWrapper);
    interscrollerWrapper.appendChild(interscrollerContainer);
    interscrollerContainer.appendChild(interscrollerOverlay);
    if (additionalElementToAdd) {
      interscrollerOverlay.appendChild(additionalElementToAdd);
      if (!isInsideAnIframe) {
        additionalElementToAdd.style.cssText += `
          position: fixed;
          top: 50vh;
          transform: translate(0px, -130%);
        `;
      }
    }
    interscrollerOverlay.appendChild(this.adDiv);
  }

  /**
   * Create side ads alongside the main ad
   * @param hideLabel
   * @param numberOfSideAds
   */
  private async createSideAds(hideLabel: boolean, numberOfSideAds: number) {
    this.advertisementID += 'a';
    const letters = 'bcdefghijklmnopqrstuvwxyz';
    const [adWrapper, adDiv] = this.createWrapperElement(hideLabel);
    this.wrapper = adWrapper;
    this.adDiv = adDiv;
    this.wrapper.style.display = 'flex';
    this.wrapper.style.justifyContent = 'center';
    this.wrapper.style.flexWrap = 'wrap';
    for (let i = 0; i < numberOfSideAds; i++) {
      const sideAd = document.createElement('div');
      let sideAdDivID = this.advertisementID;
      sideAdDivID = sideAdDivID.substring(0, sideAdDivID.length - 1) + letters[i];
      sideAd.id = sideAdDivID;
      sideAd.style.marginLeft = '3px';
      this.wrapper.appendChild(sideAd);
      this.additionalAds.push(sideAd);
    }
  }

  /**
   * Are ads on the page or not
   * @returns
   */
  private areAdsPresent() {
    return Boolean(this.wrapper.querySelector('div[id*="google_ads_iframe"]'));
  }

  /** Used for side ads */
  protected additionalAds: HTMLDivElement[] = [];

  // Try to refresh the ad if there was no created ad iframe in place for the first 10 seconds
  public refreshAdUntilVisible() {
    let numberOfRefreshes = 0;
    const adRefreshInterval = setInterval(() => {
      if (!PrebidAdvertisementUnit.prebidLoaded || !PrebidAdvertisementUnit.initialPeriodPassed) {
        return;
      }

      let shouldReturn = false;

      import(/* webpackChunkName: "abd" */ './AdblockDetector').then((adblockDetector) => {
        if (PrebidAdvertisementUnit.prebidBlocked || adblockDetector.default.adblockDetected) {
          clearInterval(adRefreshInterval);
          shouldReturn = true;
          return;
        }
      });

      if (shouldReturn) {
        return;
      }

      if (PrebidAdvertisementUnit.noGPT) {
        clearInterval(adRefreshInterval);
        return;
      }

      if (!this.wrapper.offsetParent) {
        clearInterval(adRefreshInterval);
        return;
      }

      if (!this.areAdsPresent()) {
        this.refreshAd();
        numberOfRefreshes++;
        vdebugLog(
          'info',
          true,
          `Vuukle - ${getFormattedTime()} [Platform.js] Refreshing ad: ${numberOfRefreshes}`,
          this.advertisementID
        );
      } else {
        clearInterval(adRefreshInterval);
      }

      if (numberOfRefreshes === 5) {
        clearInterval(adRefreshInterval);
      }
    }, 10000);
  }

  /** Initialize vuuklehb que for endless mode */
  public initVuukleHBQueue() {
    window.vuuklehb = window.vuuklehb || {};
    window.vuuklehb.que = window.vuuklehb.que || [];
    window.vuuklehb.que.push(() => {
      if (window.vuuklehb.vInitSlot) {
        window.vuuklehb.vInitSlot(this.advertisementID);
      }
    });
  }

  /** Refresh ad slot */
  public refreshAd() {
    if (window.vuuklehb && window.vuuklehb.vRefresh) {
      window.vuuklehb.vRefresh(this.advertisementID, true);
    }
  }

  /**
   * Create AD wrapper HTML Element which we will add to the DOM to display AD
   * @param hideLabel
   */
  protected createWrapperElement(hideLabel: boolean = false): [HTMLDivElement, HTMLDivElement] {
    /**
     * Use superclass method to generate base adWrapper that will already contain
     * Ad div with needed ID
     */
    const adDivs = super.createWrapperElement(hideLabel);
    const adWrapper = adDivs[0];
    const adScript = adWrapper.querySelector('script');

    /** We don't need the script tag */
    if (adScript instanceof HTMLScriptElement && adScript.parentNode) {
      adScript.parentNode.removeChild(adScript);
    }

    /** For ads that scroll inside their container div, make them sticky positioned */
    if (this.scrollingAd) {
      adWrapper.style.position = isiOSVersion(12) || isiOSVersion(11) || isiOSVersion(10) ? '-webkit-sticky' : 'sticky';
      adWrapper.style.top = '0';
      adWrapper.style.clear = 'both';
      /** parentElement of adWrapper is null for some reason when we try to access it from here which is why we use a setTimeout */
      setTimeout(() => {
        let wrapperAncestor = adWrapper.parentElement;
        /** To make sticky positioned ads work, the overflow property must not be set to 'hidden' on any of the parent divs */
        while (wrapperAncestor && wrapperAncestor.tagName !== 'BODY') {
          wrapperAncestor.style.setProperty('overflow', 'visible', 'important');
          wrapperAncestor = wrapperAncestor.parentElement;
        }
      }, 0);
    }

    return [adWrapper, adDivs[1]];
  }

  /**
   * Watch the ad div(s) for changes and resize the label accordingly
   * @param adWrapper
   * @param adDiv
   */
  protected watchAdDiv(adWrapper: HTMLElement, adDiv: HTMLElement): void {
    let adIframes: any[] = [() => adDiv.firstElementChild?.querySelector('iframe')];
    this.additionalAds.forEach((ad) => {
      const iframe = () => ad.firstElementChild?.querySelector('iframe');
      adIframes.push(iframe);
    });
    /**
     * Ad label resize callback
     * ResizeObserver can cause performance issues related to the INP Core Web Vitals metric
     * Which is why we're using a setTimeout here
     * @see https://web.dev/articles/manually-diagnose-slow-interactions-in-the-lab#how_to_identify_presentation_delays
     */
    const callback = () => {
      scheduler.postTask(() => {
        const label: HTMLDivElement | null = adWrapper.querySelector('.vuukle-ad-label');
        // if there's an ad, display label and adjust label width to match the ad width
        if (label) {
          let totalWidth = 0;
          adIframes.forEach((adIframe) => {
            const evaluatedIframe = adIframe();
            const iframeWidth = evaluatedIframe?.width;
            const adWidth = iframeWidth ? Math.max(parseInt(iframeWidth), 80) : 80;
            totalWidth += adWidth || 0;
            label.style.width = totalWidth + 'px';
            evaluatedIframe?.style.setProperty('margin', '0 auto'); // enforce ad to be centered
            evaluatedIframe?.style.setProperty('width', `${iframeWidth}px`, 'important');
            if (advancedKeys[this.articleConfig.apiKey].enforceAdHeight) {
              const iframeHeight = evaluatedIframe?.height;
              evaluatedIframe?.style.setProperty('height', `${iframeHeight}px`, 'important');
            }
          });
        }
      });
    };

    /** Use ResizeObserver with a fallback to Mutation Observer */
    if (typeof window.ResizeObserver !== 'undefined') {
      const resizeObserver = new window.ResizeObserver(callback);
      resizeObserver.observe(adDiv);
      this.additionalAds.forEach((div) => {
        if (div) {
          resizeObserver.observe(div);
        }
      });

      /** Resize ad container on ad change */
      // if (this.scrollingAd) {
      //   const wrapperParent = adWrapper.parentElement;

      //   if (wrapperParent) {
      //     wrapperParent.style.height = `${adWrapper.offsetHeight * 2}px`;
      //     const wrapperObserver = new ResizeObserver(() => {
      //       wrapperParent.style.height = `${adWrapper.offsetHeight * 2}px`;
      //     });
      //     wrapperObserver.observe(adWrapper);
      //   }
      // }
    }

    // Sometimes the size of the element may not change, which is why we add an additional MutationObserver

    /** Use MutationObserver to resize the ad label element every time the ad container updates */
    const config = { childList: true, subtree: true };
    // Create an observer instance linked to the callback function
    const observer = new MutationObserver(callback);
    // Start observing the target node for configured mutations
    observer.observe(adDiv, config);
    this.additionalAds.forEach((div) => {
      if (div) {
        observer.observe(div);
      }
    });

    if (this.scrollingAd) {
      const wrapperParent = adWrapper.parentElement;
      if (wrapperParent) {
        wrapperParent.style.height = Session.isMobileUser ? '348px' : '424px'; // Same height as scrolling ads from another website
        wrapperParent.style.clear = 'both';
        // const wrapperObserver = new MutationObserver(() => {
        //   wrapperParent.style.height = `${adWrapper.offsetHeight * 2}px`;
        // });
        // wrapperObserver.observe(adWrapper, config);
      }
    }
  }
}

export default PrebidAdvertisementUnit;
